import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/Purchase/indentAmendVerificationAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchIndentAmendQueue = createAsyncThunk(
    'indentAmendVerification/fetchQueue',
    async ({ roleId, username }, { rejectWithValue }) => {
        try { return await api.getVerifyIndentAmendQueue(roleId, username); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch indent amendment queue')); }
    }
);

export const fetchIndentAmendDetail = createAsyncThunk(
    'indentAmendVerification/fetchDetail',
    async ({ cccode, amendId, indentNo }, { rejectWithValue }) => {
        try { return await api.getIndentAmendByNo(cccode, amendId, indentNo); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch amendment details')); }
    }
);

export const submitIndentAmendVerification = createAsyncThunk(
    'indentAmendVerification/submit',
    async (payload, { rejectWithValue }) => {
        try { return await api.approveIndentAmend(payload); }
        catch (e) { return rejectWithValue(err(e, 'Failed to submit verification')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailLoading: false, detailError: null,
    verifyLoading: false, verifyError: null, verifyResult: null,
};

const indentAmendVerificationSlice = createSlice({
    name: 'indentAmendVerification',
    initialState,
    reducers: {
        clearIndentAmendDetail: (s) => { s.detail = null; s.detailError = null; },
        clearVerifyResult: (s) => { s.verifyResult = null; s.verifyError = null; },
        resetIndentAmendVerification: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchIndentAmendQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchIndentAmendQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchIndentAmendQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        builder
            .addCase(fetchIndentAmendDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; })
            .addCase(fetchIndentAmendDetail.fulfilled, (s, a) => { s.detailLoading = false; s.detail = a.payload?.Data || null; })
            .addCase(fetchIndentAmendDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });

        builder
            .addCase(submitIndentAmendVerification.pending, (s) => { s.verifyLoading = true; s.verifyError = null; })
            .addCase(submitIndentAmendVerification.fulfilled, (s, a) => { s.verifyLoading = false; s.verifyResult = a.payload?.Data ?? a.payload; })
            .addCase(submitIndentAmendVerification.rejected, (s, a) => { s.verifyLoading = false; s.verifyError = a.payload; });
    },
});

export const {
    clearIndentAmendDetail, clearVerifyResult, resetIndentAmendVerification,
} = indentAmendVerificationSlice.actions;

export const selectIAQueue = (s) => s.indentAmendVerification.queue;
export const selectIAQueueLoading = (s) => s.indentAmendVerification.queueLoading;
export const selectIAQueueError = (s) => s.indentAmendVerification.queueError;
export const selectIADetail = (s) => s.indentAmendVerification.detail;
export const selectIADetailLoading = (s) => s.indentAmendVerification.detailLoading;
export const selectIAVerifyLoading = (s) => s.indentAmendVerification.verifyLoading;

export default indentAmendVerificationSlice.reducer;
